import React, { useState } from 'react';
import styled from 'styled-components';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { usePermissions } from '../hooks/usePermissions';
import { 
  useGetOrganizationsQuery, 
  useGetOrganizationByIdQuery, 
  useCreateOrganizationMutation,
  useUpdateOrganizationMutation,
  useDeleteOrganizationMutation,
  OrganizationFilters, 
  OrganizationStats
} from '../store/api/organizationsApi';
import { useGetBusinessSystemsQuery } from '../store/api/businessSystemsApi';
import { Card, Button, Input, Select, Badge, EmptyState, LoadingSpinner, Table, TableHeader, TableCell, TableRow } from '../styles/components';
import { OrganizationDetails } from '../components/organizations/OrganizationDetails';
import { OrganizationForm, OrganizationFormData } from '../components/organizations/OrganizationForm';
import { Modal } from '../components/common/Modal';
import { OrganizationCardsSkeleton, OrganizationDetailsSkeleton } from '../components/common/LoadingStates';
import { downloadOrganizationsCSV } from '../utils/export';
import { 
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  BuildingOfficeIcon,
  UsersIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';

// Helper function to check if error has data property with errors array
function isFetchBaseQueryErrorWithErrors(error: any): error is FetchBaseQueryError & { data: { errors?: string[] } } {
  return typeof error === 'object' && error !== null && 'data' in error && 
         typeof error.data === 'object' && error.data !== null;
}

const OrganizationsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xl};
`;

const OrganizationsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const HeaderContent = styled.div``;

const OrganizationsTitle = styled.h1`
  font-size: ${props => props.theme.fontSizes['2xl']};
  font-weight: ${props => props.theme.fontWeights.bold};
  color: ${props => props.theme.colors.gray[900]};
`;

const OrganizationsSubtitle = styled.p`
  margin-top: ${props => props.theme.spacing.xs};
  color: ${props => props.theme.colors.gray[500]};
`;

const HeaderActions = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
`;

const CreateButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  
  svg {
    height: 1rem;
    width: 1rem;
  }
`;

const ExportButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  background-color: ${props => props.theme.colors.gray[100]};
  color: ${props => props.theme.colors.gray[700]};
  
  &:hover:not(:disabled) {
    background-color: ${props => props.theme.colors.gray[200]};
  }
  
  svg {
    height: 1rem;
    width: 1rem;
  }
`;

const FilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.xl};
  
  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
  
  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  
  svg {
    position: absolute;
    left: ${props => props.theme.spacing.sm};
    top: 50%;
    transform: translateY(-50%);
    height: 1.25rem;
    width: 1.25rem;
    color: ${props => props.theme.colors.gray[400]};
  }
`;

const SearchInput = styled(Input)`
  padding-left: ${props => props.theme.spacing.xl};
  width: 100%;
  
  @media (min-width: 768px) {
    width: 20rem;
  }
`;

const FilterSelect = styled(Select)`
  width: 100%;
  
  @media (min-width: 768px) {
    width: 15rem;
  }
`;

const ResultsCount = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  color: ${props => props.theme.colors.gray[500]};
  font-size: ${props => props.theme.fontSizes.sm};
  
  svg {
    height: 1rem;
    width: 1rem;
  }
`;

const TableContainer = styled(Card)`
  padding: 0;
  overflow: hidden;
`;

const TableWrapper = styled.div`
  overflow-x: auto;
`;

const StyledTable = styled(Table)`
  min-width: 100%;
`;

const TableHead = styled.thead`
  background-color: ${props => props.theme.colors.gray[50]};
`;

const TableBody = styled.tbody`
  background-color: white;
`;

const ClickableRow = styled(TableRow)`
  cursor: pointer;

  &:hover {
    background-color: ${props => props.theme.colors.gray[50]};
  }
`;

const OrganizationNameCell = styled.div`
  font-weight: ${props => props.theme.fontWeights.medium};
  color: ${props => props.theme.colors.primary[600]};
`;

const OrganizationIdText = styled.div`
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.gray[500]};
  font-family: monospace;
  margin-top: ${props => props.theme.spacing.xs};
`;

const LoadingCard = styled(Card)`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 16rem;
  color: ${props => props.theme.colors.gray[500]};
`;

const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: ${props => props.theme.spacing.xl};
  padding: ${props => props.theme.spacing.md};
  background: white;
  border-radius: ${props => props.theme.borders.radius.md};
  border: 1px solid ${props => props.theme.colors.gray[200]};
`;

const PaginationInfo = styled.div`
  flex: 1;
  color: ${props => props.theme.colors.gray[500]};
  font-size: ${props => props.theme.fontSizes.sm};
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
`;

const PageButton = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  min-width: 2.5rem;
  height: 2.5rem;
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.medium};
  border-radius: ${props => props.theme.borders.radius.md};
  border: 1px solid ${props => props.theme.colors.gray[200]};
  background: ${props => props.$active ? props.theme.colors.primary[50] : 'white'};
  color: ${props => props.$active ? props.theme.colors.primary[600] : props.theme.colors.gray[500]};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.$active ? props.theme.colors.primary[100] : props.theme.colors.gray[50]};
    border-color: ${props => props.$active ? props.theme.colors.primary[300] : props.theme.colors.gray[300]};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  svg {
    height: 1.25rem;
    width: 1.25rem;
  }
`;

export function Organizations() {
  const { isSupport, isSuperAdmin } = usePermissions();
  const [page, setPage] = useState(1);
  const [selectedOrganization, setSelectedOrganization] = useState<string | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingOrganization, setEditingOrganization] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [filters, setFilters] = useState<OrganizationFilters>({
    page: 1,
    pageSize: 20,
    search: '',
    businessSystemId: undefined,
    sortBy: 'name',
    sortDescending: false,
  });

  const { data: businessSystems } = useGetBusinessSystemsQuery();

  const handleFilterChange = (key: keyof OrganizationFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
    setPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  const handleOrganizationClick = (organizationId: string) => {
    setSelectedOrganization(organizationId);
  };

  const handleBackToList = () => {
    setSelectedOrganization(null);
    setEditingOrganization(null);
    setIsFormOpen(false);
  };

  const handleEditOrganization = () => {
    if (selectedOrganization) {
      setEditingOrganization(selectedOrganization);
      setIsFormOpen(true);
    }
  };

  const handleAddOrganization = () => {
    setEditingOrganization(null);
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setEditingOrganization(null);
  };

  const handleFormSubmit = async (data: OrganizationFormData) => {
    try {
      if (editingOrganization) {
        await updateOrganization({ 
          id: editingOrganization, 
          ...data 
        }).unwrap();
      } else {
        await createOrganization(data).unwrap();
      }
      handleCloseForm();
    } catch (error) {
      // Error is handled by RTK Query and displayed in the form
    }
  };

  const handleDeleteOrganization = async (org: OrganizationStats) => {
    if (org.isDeleted) {
      alert(`Organization '${org.name}' is already deleted.`);
      return;
    }

    // Check if organization has active clients
    if (org.activeApiClients > 0) {
      alert(
        `Cannot delete organization '${org.name}'.\n\n` +
        `It has ${org.activeApiClients} active client(s).\n\n` +
        `Please lock or deactivate all clients before deleting the organization.`
      );
      return;
    }

    if (window.confirm(
      `Are you sure you want to delete organization '${org.name}'?\n\n` +
      `This action cannot be undone.`
    )) {
      try {
        await deleteOrganization(org.id).unwrap();
        if (selectedOrganization === org.id) {
          setSelectedOrganization(null);
        }
      } catch (error: any) {
        // Handle error from backend
        const errorMessage = error?.data?.errors?.[0] || 'Failed to delete organization';
        alert(`Error: ${errorMessage}`);
        console.error('Failed to delete organization:', error);
      }
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await downloadOrganizationsCSV({
        search: filters.search || undefined,
        businessSystemId: filters.businessSystemId || undefined,
        sortBy: filters.sortBy || undefined,
        sortDescending: filters.sortDescending
      });
    } catch (error) {
      console.error('Export failed:', error);
      // You could show a toast notification here
    } finally {
      setIsExporting(false);
    }
  };

  const queryParams = {
    ...filters,
    page,
    businessSystemId: filters.businessSystemId || undefined,
  };

  const { data, isLoading, error } = useGetOrganizationsQuery(queryParams);
  const { data: selectedOrgData, isLoading: selectedOrgLoading } = useGetOrganizationByIdQuery(
    selectedOrganization!, 
    { skip: !selectedOrganization }
  );
  const { data: editingOrgData } = useGetOrganizationByIdQuery(
    editingOrganization!,
    { skip: !editingOrganization }
  );

  const [createOrganization, { isLoading: isCreating, error: createError }] = useCreateOrganizationMutation();
  const [updateOrganization, { isLoading: isUpdating, error: updateError }] = useUpdateOrganizationMutation();
  const [deleteOrganization, { isLoading: isDeleting }] = useDeleteOrganizationMutation();

  // Only show to support users
  if (!isSupport) {
    return (
      <OrganizationsContainer>
        <EmptyState>
          <UsersIcon />
          <h3>Access Restricted</h3>
          <p>Organization management is only available to support users.</p>
        </EmptyState>
      </OrganizationsContainer>
    );
  }

  if (isLoading) {
    return (
      <OrganizationsContainer>
        <OrganizationsHeader>
          <HeaderContent>
            <OrganizationsTitle>Organizations</OrganizationsTitle>
            <OrganizationsSubtitle>
              Manage organizations and their API access
            </OrganizationsSubtitle>
          </HeaderContent>
          <CreateButton>
            <PlusIcon />
            Add Organization
          </CreateButton>
        </OrganizationsHeader>
        <OrganizationCardsSkeleton />
      </OrganizationsContainer>
    );
  }

  if (error) {
    return (
      <OrganizationsContainer>
        <EmptyState>
          <BuildingOfficeIcon />
          <h3>Error Loading Organizations</h3>
          <p>Please try again later.</p>
        </EmptyState>
      </OrganizationsContainer>
    );
  }

  // Show organization details if one is selected and data matches
  if (selectedOrganization && selectedOrgData && selectedOrgData.id === selectedOrganization) {
    return (
      <OrganizationsContainer>
        <OrganizationDetails
          organization={selectedOrgData}
          onBack={handleBackToList}
          onDelete={() => handleDeleteOrganization(selectedOrgData)}
        />
      </OrganizationsContainer>
    );
  }

  // Show loading state for selected organization
  if (selectedOrganization) {
    return (
      <OrganizationsContainer>
        <OrganizationDetailsSkeleton />
      </OrganizationsContainer>
    );
  }

  return (
    <OrganizationsContainer>
      <OrganizationsHeader>
        <HeaderContent>
          <OrganizationsTitle>Organizations</OrganizationsTitle>
          <OrganizationsSubtitle>
            Manage organizations and their API access
          </OrganizationsSubtitle>
        </HeaderContent>
        <HeaderActions>
          <ExportButton onClick={handleExport} disabled={isExporting}>
            <ArrowDownTrayIcon />
            {isExporting ? 'Exporting...' : 'Export CSV'}
          </ExportButton>
          <CreateButton onClick={handleAddOrganization}>
            <PlusIcon />
            Add Organization
          </CreateButton>
        </HeaderActions>
      </OrganizationsHeader>

      <FilterContainer>
        <FilterGroup>
          <SearchContainer>
            <MagnifyingGlassIcon />
            <SearchInput
              placeholder="Search organizations..."
              value={filters.search || ''}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
          </SearchContainer>
          {isSuperAdmin && (
            <FilterSelect
              value={filters.businessSystemId || ''}
              onChange={(e) => handleFilterChange('businessSystemId', e.target.value || undefined)}
            >
              <option value="">All Business Systems</option>
              {(businessSystems || []).map((bs) => (
                <option key={bs.id} value={bs.id}>
                  {bs.name}
                </option>
              ))}
            </FilterSelect>
          )}
          <FilterSelect
            value={filters.useSystemCertificate === undefined ? '' : String(filters.useSystemCertificate)}
            onChange={(e) => handleFilterChange('useSystemCertificate', e.target.value === '' ? undefined : e.target.value === 'true')}
          >
            <option value=""></option>
            <option value="true">Uses System Certificate</option>
            <option value="false">Organization's Certificate</option>
          </FilterSelect>
          <FilterSelect
            value={filters.sortBy || 'name'}
            onChange={(e) => handleFilterChange('sortBy', e.target.value)}
          >
            <option value="name">Sort by Name</option>
            <option value="created">Sort by Created</option>
            <option value="clientcount">Sort by API Clients</option>
            <option value="managementusers">Sort by Management Users</option>
            <option value="lastactivity">Sort by Last Activity</option>
          </FilterSelect>
        </FilterGroup>
        
        <ResultsCount>
          <FunnelIcon />
          {data?.totalCount || 0} organizations
        </ResultsCount>
      </FilterContainer>

      {!data?.items.length ? (
        <EmptyState>
          <BuildingOfficeIcon />
          <h3>No organizations found</h3>
          <p>Try adjusting your search criteria or add a new organization.</p>
        </EmptyState>
      ) : (
        <>
          <TableContainer>
            <TableWrapper>
              <StyledTable>
                <TableHead>
                  <tr>
                    <TableHeader>Name</TableHeader>
                    <TableHeader>Organization ID</TableHeader>
                    <TableHeader>Business System</TableHeader>
                    <TableHeader>System Certificate</TableHeader>
                  </tr>
                </TableHead>
                <TableBody>
                  {data.items.map((organization) => (
                    <ClickableRow
                      key={organization.id}
                      onClick={() => handleOrganizationClick(organization.id)}
                    >
                      <TableCell>
                        <OrganizationNameCell>{organization.name}</OrganizationNameCell>
                      </TableCell>
                      <TableCell>
                        <OrganizationIdText>{organization.organizationId}</OrganizationIdText>
                      </TableCell>
                      <TableCell>
                        {organization.businessSystemName || '-'}
                      </TableCell>
                      <TableCell>
                        <Badge variant={organization.useSystemCertificate ? 'success' : 'info'}>
                          {organization.useSystemCertificate ? 'Yes' : 'No'}
                        </Badge>
                      </TableCell>
                    </ClickableRow>
                  ))}
                </TableBody>
              </StyledTable>
            </TableWrapper>
          </TableContainer>

          <PaginationContainer>
            <PaginationInfo>
              Showing {((page - 1) * 20) + 1} to {Math.min(page * 20, data.totalCount)} of {data.totalCount} results
            </PaginationInfo>

            <PaginationControls>
              <PageButton
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
              >
                <ChevronLeftIcon />
              </PageButton>

              {Array.from({ length: Math.min(5, data.totalPages || 1) }, (_, i) => {
                const pageNumber = Math.max(1, page - 2) + i;
                if (pageNumber > (data.totalPages || 1)) return null;

                return (
                  <PageButton
                    key={pageNumber}
                    $active={pageNumber === page}
                    onClick={() => handlePageChange(pageNumber)}
                  >
                    {pageNumber}
                  </PageButton>
                );
              })}

              <PageButton
                onClick={() => handlePageChange(page + 1)}
                disabled={page === (data.totalPages || 1)}
              >
                <ChevronRightIcon />
              </PageButton>
            </PaginationControls>
          </PaginationContainer>
        </>
      )}

      <Modal isOpen={isFormOpen} onClose={handleCloseForm}>
        <OrganizationForm
          organization={editingOrgData}
          isOpen={isFormOpen}
          onClose={handleCloseForm}
          onSubmit={handleFormSubmit}
          isLoading={isCreating || isUpdating}
          error={
            (createError && isFetchBaseQueryErrorWithErrors(createError) ? createError.data?.errors?.[0] : undefined) ||
            (updateError && isFetchBaseQueryErrorWithErrors(updateError) ? updateError.data?.errors?.[0] : undefined)
          }
        />
      </Modal>
    </OrganizationsContainer>
  );
}